import React, { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  Divider,
  Grid,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { Icon } from "@iconify/react";
import dayjs from "dayjs";
import {
  useGetEventsQuery,
  useDeleteEventMutation,
} from "../features/events/eventsApiSlice";
import TestingEventFormDialog from "../components/events/TestingEventFormDialog";
import AttendeesDialog from "../components/events/AttendeesDialog";
import TestingAttendeesList from "../components/events/TestingAttendeesList";
import TestingFeeCollectionDialog from "../components/events/TestingFeeCollectionDialog";
import { useDispatch, useSelector } from "react-redux";
import { setDialog } from "../features/dialog/dialogSlice";
import { Route, Routes, useNavigate } from "react-router-dom";
import { selectCurrentUserData } from "../features/auth/authSlice";
import useAuth from "../hooks/useAuth";

export default function TestingsPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userData = useSelector(selectCurrentUserData);
  const { isAdmin, isSuperAdmin } = useAuth();
  const currentUserId = String(userData?.id || userData?._id || "");

  const {
    data: events = [],
    isLoading,
    isError,
    refetch,
  } = useGetEventsQuery();
  const [deleteEvent, { isLoading: isDeleting }] = useDeleteEventMutation();

  const testingEvents = useMemo(
    () =>
      Array.isArray(events)
        ? events.filter((e) => e?.eventType === "TESTING")
        : [],
    [events]
  );

  const [openForm, setOpenForm] = useState(false);
  const [editEvent, setEditEvent] = useState(null);

  const [openAttendees, setOpenAttendees] = useState(false);
  const [activeEvent, setActiveEvent] = useState(null);
  const [openFeeCollection, setOpenFeeCollection] = useState(false);
  const [feeEvent, setFeeEvent] = useState(null);

  const handleCreate = () => {
    setEditEvent(null);
    setOpenForm(true);
  };

  const handleEdit = (ev) => {
    setEditEvent(ev);
    setOpenForm(true);
  };

  const handleAfterSave = () => {
    setOpenForm(false);
    setEditEvent(null);
    refetch();
  };

  const handleOpenAttendees = (ev) => {
    setActiveEvent(ev);
    setOpenAttendees(true);
  };

  const handleAttendeesClosed = (changed) => {
    setOpenAttendees(false);
    setActiveEvent(null);
    if (changed) refetch();
  };

  const handleOpenFeeCollection = (ev) => {
    setFeeEvent(ev);
    setOpenFeeCollection(true);
  };

  const handleCloseFeeCollection = () => {
    setOpenFeeCollection(false);
    setFeeEvent(null);
  };

  const handleDelete = async (ev) => {
    const ok = window.confirm(`Prüfung "${ev.eventName}" wirklich löschen?`);
    if (!ok) return;
    try {
      await deleteEvent(ev._id || ev.id).unwrap();
      dispatch(
        setDialog({
          type: "SUCCESS",
          title: "Prüfung gelöscht",
          data: { successText: `${ev.eventName} wurde gelöscht.` },
        })
      );
      refetch();
    } catch (e) {
      dispatch(
        setDialog({
          type: "ERROR",
          title: "Fehler beim Löschen",
          data: { errorText: e?.data?.message || "Löschen fehlgeschlagen." },
        })
      );
    }
  };

  const openListView = (ev) => {
    const id = ev._id || ev.id;
    if (!id) return;
    navigate(String(id));
  };

  return (
    <>
      <Helmet>
        <title> Dashboard: Prüfungen </title>
      </Helmet>

      <Container sx={{ pb: 4 }}>
        <Routes>
          <Route
            index
            element={
              <>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 2 }}
                >
                  <Typography variant="h4">Prüfungen</Typography>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleCreate}
                    startIcon={<Icon icon="mdi:plus" />}
                  >
                    Neue Prüfung
                  </Button>
                </Stack>

                <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                  {isLoading && (
                    <>
                      <CircularProgress size={18} />
                      <Typography variant="body2">Laden…</Typography>
                    </>
                  )}
                  {isError && (
                    <Typography color="error">
                      Fehler beim Laden.{' '}
                      <Button size="small" onClick={refetch}>
                        Erneut laden
                      </Button>
                    </Typography>
                  )}
                  {isDeleting && <Typography variant="body2">Lösche…</Typography>}
                </Box>

                <Grid container spacing={2}>
                  {testingEvents.map((ev) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={ev._id || ev.id}>
                      <EventCard
                        event={ev}
                        canEdit={isAdmin || isSuperAdmin || String(ev.creator) === currentUserId}
                        onEdit={() => handleEdit(ev)}
                        onDelete={() => handleDelete(ev)}
                        onManageAttendees={() => handleOpenAttendees(ev)}
                        onShowList={() => openListView(ev)}
                        onCollectTestingFee={() => handleOpenFeeCollection(ev)}
                      />
                    </Grid>
                  ))}

                  {!isLoading && testingEvents.length === 0 && (
                    <Grid item xs={12}>
                      <EmptyState onCreate={handleCreate} />
                    </Grid>
                  )}
                </Grid>
              </>
            }
          />
          <Route path=":testingId" element={<TestingAttendeesList />} />
        </Routes>
      </Container>

      {/* Create/Edit dialog */}
      <Dialog open={openForm} onClose={() => setOpenForm(false)} maxWidth="sm" fullWidth>
        <TestingEventFormDialog
          onClose={() => setOpenForm(false)}
          onSaved={handleAfterSave}
          initialEvent={editEvent}
        />
      </Dialog>

      {/* Manage attendees */}
      {!!activeEvent && (
        <AttendeesDialog
          open={openAttendees}
          onClose={handleAttendeesClosed}
          eventId={activeEvent._id || activeEvent.id}
          eventTitle={activeEvent.eventName}
        />
      )}

      {/* Testing fee collection */}
      {!!feeEvent && (
        <TestingFeeCollectionDialog
          open={openFeeCollection}
          onClose={handleCloseFeeCollection}
          event={feeEvent}
        />
      )}
    </>
  );
}

const EventCard = ({
  event,
  canEdit,
  onEdit,
  onDelete,
  onManageAttendees,
  onShowList,
  onCollectTestingFee,
}) => {
  const begin = event?.eventDateBegin
    ? dayjs(event.eventDateBegin).format("DD.MM.YYYY HH:mm")
    : "";
  const end = event?.eventDateEnd
    ? dayjs(event.eventDateEnd).format("DD.MM.YYYY HH:mm")
    : null;
  const attendeesCount = Array.isArray(event?.hasAttendees)
    ? event.hasAttendees.length
    : 0;

  return (
    <Card variant="outlined" sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <CardContent sx={{ flex: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Chip size="small" color="secondary" icon={<Icon icon="mdi:school" />} label="TESTING" sx={{ fontWeight: 600 }} />
          <Typography variant="h6" sx={{ ml: 1 }}>
            {event.eventName}
          </Typography>
        </Stack>

        <Stack spacing={0.5} sx={{ color: "text.secondary" }}>
          <InfoRow icon="mdi:calendar-clock" text={end ? `${begin} – ${end}` : begin} />
          {event?.locationName && <InfoRow icon="mdi:map-marker" text={event.locationName} />}
          <InfoRow icon="mdi:account-group" text={`Teilnehmer: ${attendeesCount}`} bold />
        </Stack>
      </CardContent>
      <Divider />
      <CardActions sx={{ justifyContent: "space-between" }}>
        <Stack direction="column" width="100%" spacing={1}>
          {canEdit && (
            <Button size="large" fullWidth variant="outlined" onClick={onManageAttendees} startIcon={<Icon icon="mdi:account-multiple-plus" />}>
              Teilnehmer
            </Button>
          )}
          <Button size="large" variant="outlined" onClick={onShowList} startIcon={<Icon icon="mdi:table" />}>
            Teilnehmerliste
          </Button>
          {canEdit && (
            <Tooltip
              title={
                attendeesCount > 0
                  ? "SEPA-Lastschrift für Prüfungsgebühren erzeugen"
                  : "Keine Teilnehmer auf der Liste"
              }
            >
              <span>
                <Button
                  size="large"
                  variant="outlined"
                  onClick={onCollectTestingFee}
                  startIcon={<Icon icon="mdi:bank-transfer" />}
                  disabled={attendeesCount === 0}
                >
                  Einzug Prüfungsgebühr
                </Button>
              </span>
            </Tooltip>
          )}
          {canEdit && (
            <Button size="large" variant="outlined" onClick={onEdit} startIcon={<Icon icon="mdi:pencil" />}>
              Bearbeiten
            </Button>
          )}
          {canEdit && (
            <Tooltip title="Löschen">
              <Button size="large" variant="outlined" color="error" onClick={onDelete} startIcon={<Icon icon="mdi:trash-can-outline" />}>
                Löschen
              </Button>
            </Tooltip>
          )}
        </Stack>
      </CardActions>
    </Card>
  );
};

const InfoRow = ({ icon, text, bold }) => (
  <Stack direction="row" spacing={1} alignItems="center">
    <Icon icon={icon} width={18} />
    <Typography variant="body2" sx={{ fontWeight: bold ? 600 : 400 }}>
      {text}
    </Typography>
  </Stack>
);

const EmptyState = ({ onCreate }) => (
  <Card variant="outlined" sx={{ p: 3, textAlign: "center" }}>
    <Typography variant="h6" sx={{ mb: 1 }}>
      Noch keine Prüfungen
    </Typography>
    <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
      Legen Sie eine neue Prüfung an oder fügen Sie Teilnehmer über die Mitgliederliste hinzu.
    </Typography>
    <Button variant="contained" color="secondary" onClick={onCreate} startIcon={<Icon icon="mdi:plus" />}>
      Neue Prüfung
    </Button>
  </Card>
);
